import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FileDoc, User } from 'lib/model';
import { CloudDownloadIcon } from '@heroicons/react/outline';
import Script from 'next/script';
import {
  collection,
  deleteField,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useInterval } from 'lib/useInterval';
import { minutesFromNow, reportError, secondsSince } from 'lib/util';
import { AnnotationIcon } from '@heroicons/react/solid';
import { Dialog } from '@headlessui/react';
import TransitionDialog from 'components/transition-dialog';
import LinkDriveButton from 'components/link-drive-button';
import { User as AuthUser } from '@firebase/auth';
import MessageDialog, { DialogConfig } from 'components/message-dialog';
import { friendlyErrorMessage } from 'components/friendly-error';
import { usePrevious } from 'lib/usePrevious';
import { LoadingSpinner } from 'components/loading-spinner';

interface Props {
  user?: User;
  authUser?: AuthUser;
}

export default function FileUploadFromDriveButton({
  user,
  authUser,
}: Props): ReactElement {
  const previousUser = usePrevious(user);
  const [gapiLoaded, setGapiLoaded] = useState(false);
  const [loadingPicker, setLoadingPicker] = useState(false);
  const [updateTokenTriggered, setUpdateTokenTriggered] = useState<Date>();
  const [linkDriveDialogOpen, setLinkDriveDialogOpen] = useState(false);
  const linkDriveCancelButtonRef = useRef(null);
  const [messageDialogConfig, setMessageDialogConfig] =
    useState<DialogConfig>();

  useEffect(() => {
    setGapiLoaded(typeof gapi === 'object');
  }, []);

  function couldNotOpenPickerError() {
    setMessageDialogConfig({
      title: 'Could not open Google Drive file picker',
      message: friendlyErrorMessage,
    });
  }

  const loadPicker = useCallback(() => {
    function isAudioVideo(driveItem: google.picker.DocumentObject) {
      const mimeType = driveItem[google.picker.Document.MIME_TYPE];
      return mimeType.startsWith('audio') || mimeType.startsWith('video');
    }

    function isAlreadyUploaded(
      driveItem: google.picker.DocumentObject,
      fileDocs: FileDoc[]
    ) {
      return fileDocs.some(
        (f) => f.ref.id === driveItem[google.picker.Document.ID]
      );
    }

    async function onPicked(driveItems: google.picker.DocumentObject[]) {
      try {
        const files = await getDocs(
          query(collection(user.ref, 'files'), where('deleted', '==', null))
        );
        const fileDocs = files.docs as unknown as FileDoc[];

        const validDriveItems = driveItems.filter(
          (driveItem) =>
            isAudioVideo(driveItem) && !isAlreadyUploaded(driveItem, fileDocs)
        );

        for (const driveItem of validDriveItems) {
          await setDoc(
            doc(user.ref, 'files', driveItem[google.picker.Document.ID]),
            {
              created: serverTimestamp(),
              token: deleteField(),
              uploading: true,
              transcriptStatus: 'not_requested',
              uploadedVia: 'picker',
              name: driveItem[google.picker.Document.NAME],
              error: null,
              deleted: null,
            },
            { merge: true }
          );
        }

        const notAudioVideoItems = driveItems.filter(
          (driveItem) => !isAudioVideo(driveItem)
        );

        const alreadyUploadedItems = driveItems.filter((driveItem) =>
          isAlreadyUploaded(driveItem, fileDocs)
        );

        if (notAudioVideoItems.length > 0 || alreadyUploadedItems.length > 0) {
          const notAudioVideoMessage = notAudioVideoItems.length > 0 && (
            <div>
              <p className="font-bold italic">
                These files are not audio or video
              </p>
              <ul>
                {notAudioVideoItems
                  .map((i) => i[google.picker.Document.NAME])
                  .map((name) => (
                    <li key={name}>{name}</li>
                  ))}
              </ul>
            </div>
          );

          const alreadyUploadedMessage = alreadyUploadedItems.length > 0 && (
            <div>
              <p className="font-bold italic">
                These files have already been uploaded previously
              </p>
              <ul>
                {alreadyUploadedItems
                  .map((i) => i[google.picker.Document.NAME])
                  .map((name) => (
                    <li key={name}>{name}</li>
                  ))}
              </ul>
            </div>
          );

          setMessageDialogConfig({
            title: 'Could not upload file',
            element: (
              <>
                {notAudioVideoMessage}
                {alreadyUploadedMessage}
              </>
            ),
          });
        }
      } catch (e) {
        reportError(e);

        setMessageDialogConfig({
          title: 'Could not upload file',
          message: friendlyErrorMessage,
        });
      }
    }

    setLoadingPicker(true);

    gapi.load('client:picker', {
      callback: () => {
        try {
          new google.picker.PickerBuilder()
            .enableFeature(google.picker.Feature.SUPPORT_DRIVES)
            .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
            .setDeveloperKey(process.env.NEXT_PUBLIC_FIREBASE_API_KEY)
            .setAppId(process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID.split('-')[0])
            .setOAuthToken(user.tokens.access_token)
            .addView(
              new google.picker.DocsView(google.picker.ViewId.DOCS).setMode(
                google.picker.DocsViewMode.LIST
              )
            )
            .setCallback((result) => {
              if (
                result[google.picker.Response.ACTION] ===
                google.picker.Action.PICKED
              ) {
                onPicked(result[google.picker.Response.DOCUMENTS]);
              }
            })
            .build()
            .setVisible(true);
        } catch (e) {
          reportError(e);
          couldNotOpenPickerError();
        } finally {
          setLoadingPicker(false);
        }
      },
      onerror: () => {
        reportError('Error loading client:picker');
        couldNotOpenPickerError();
        setLoadingPicker(false);
      },
      timeout: 10_000,
      ontimeout: () => {
        reportError('Timed out loading client:picker');
        couldNotOpenPickerError();
        setLoadingPicker(false);
      },
    });
  }, [user]);

  useEffect(() => {
    if (
      (updateTokenTriggered || linkDriveDialogOpen) &&
      user.tokens?.expiry_date > Date.now()
    ) {
      setUpdateTokenTriggered(undefined);
      setLinkDriveDialogOpen(false);
      loadPicker();
    }

    if (updateTokenTriggered && previousUser.tokens && !user.tokens) {
      setUpdateTokenTriggered(undefined);
      setLinkDriveDialogOpen(true);
    }
  }, [
    user,
    previousUser,
    updateTokenTriggered,
    linkDriveDialogOpen,
    loadPicker,
  ]);

  useInterval(
    () => {
      if (updateTokenTriggered && secondsSince(updateTokenTriggered) > 60) {
        reportError('Token taking longer than 60 seconds, user: ' + user.id);
        couldNotOpenPickerError();
        setUpdateTokenTriggered(undefined);
      }
    },
    updateTokenTriggered ? 15 * 1000 : null
  );

  const loading =
    !user || !gapiLoaded || loadingPicker || !!updateTokenTriggered;

  return (
    <>
      <button
        type="button"
        className="disabled:pointer-events-auto disabled:cursor-not-allowed inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-70 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-40"
        onClick={async () => {
          if (!user.tokens) {
            setLinkDriveDialogOpen(true);
            return;
          }

          if (user.tokens.expiry_date > minutesFromNow(10).getTime()) {
            loadPicker();
            return;
          }

          try {
            setUpdateTokenTriggered(new Date());

            await updateDoc(user.ref, {
              updateTokenTriggered: serverTimestamp(),
            });
          } catch (e) {
            reportError(e);
            setUpdateTokenTriggered(undefined);
            couldNotOpenPickerError();
          }
        }}
        disabled={loading}
      >
        {loading ? (
          <div className="-ml-1 mr-3 h-5 w-5 text-white" aria-hidden="true">
            <LoadingSpinner />
          </div>
        ) : (
          <CloudDownloadIcon
            className="-ml-1 mr-2 h-5 w-5 text-white"
            aria-hidden="true"
          />
        )}
        Add files from Google Drive
      </button>

      <TransitionDialog
        open={linkDriveDialogOpen}
        initialFocus={linkDriveCancelButtonRef}
      >
        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
              <AnnotationIcon
                className="h-6 w-6 text-blue-600"
                aria-hidden="true"
              />
            </div>
            <div className="mt-3 text-center min-w-0 sm:mt-0 sm:ml-4 sm:text-left">
              <Dialog.Title
                as="h3"
                className="text-lg leading-6 font-medium text-gray-900"
              >
                Link Google Drive
              </Dialog.Title>
              <div>
                <LinkDriveButton user={user} authUser={authUser} />
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={() => setLinkDriveDialogOpen(false)}
            ref={linkDriveCancelButtonRef}
          >
            Close
          </button>
        </div>
      </TransitionDialog>
      <MessageDialog
        config={messageDialogConfig}
        setConfig={setMessageDialogConfig}
      />
      <Script
        src="https://apis.google.com/js/api.js"
        onLoad={() => setGapiLoaded(true)}
      />
    </>
  );
}
